import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { campaignReport, syncCampaignStatus } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  MailCheck,
  MailX,
  Eye,
  MousePointerClick,
  Send,
  RefreshCw,
  AlertTriangle,
  Ban,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const StatusBadge = ({ status }) => {
  const map = {
    draft: "badge-neutral",
    sending: "badge-warning",
    sent: "badge-success",
    failed: "badge-error",
  };
  return <span className={`badge ${map[status] || "badge-neutral"}`}>{status}</span>;
};

const TypeBadge = ({ type }) => {
  const map = {
    sent: "badge-neutral",
    delivered: "badge-success",
    open: "badge-neutral",
    click: "badge-warning",
    bounced: "badge-error",
    complained: "badge-error",
    delayed: "badge-warning",
    failed: "badge-error",
  };
  return <span className={`badge ${map[type] || "badge-neutral"}`}>{type}</span>;
};

export default function CampaignReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const d = await campaignReport(id);
      setData(d);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetch = () =>
      campaignReport(id).then((d) => {
        if (mounted) setData(d);
      });
    fetch();
    const int = setInterval(fetch, 6000);
    return () => {
      mounted = false;
      clearInterval(int);
    };
  }, [id]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncCampaignStatus(id);
      if (res.permission_denied) {
        toast.error(
          "Your Resend API key can't read status. Set up a Resend webhook (Settings page) to get live delivery data.",
          { duration: 8000 }
        );
      } else {
        toast.success(
          `Synced: ${res.delivered} delivered · ${res.bounced} bounced · ${res.opens} opens · ${res.clicks} clicks`
        );
      }
      await load();
    } catch (e) {
      toast.error("Failed to sync from Resend");
    }
    setSyncing(false);
  };

  if (!data) {
    return <div className="p-8 text-sm text-[#94A3B8]">Loading report…</div>;
  }
  const c = data.campaign;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="report-page">
      <Link to="/campaigns" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#002FA7] mb-4">
        <ArrowLeft size={14} /> Back to campaigns
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">Campaign report</div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]" style={{ fontFamily: "Outfit" }} data-testid="report-name">
            {c.name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <StatusBadge status={c.status} />
            <span className="text-sm text-[#475569]">{c.subject}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing || c.sent_count === 0}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="btn-sync-resend"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Sync from Resend"}
          </button>
        </div>
      </div>

      {/* Score cards - primary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Score
          testid="report-accepted"
          label="Accepted by Resend"
          value={c.sent_count}
          hint={`of ${c.total_recipients} recipients`}
          Icon={Send}
        />
        <Score
          testid="report-delivered"
          label="Delivered"
          value={c.delivered_count || 0}
          hint={`${data.delivery_rate || 0}% delivery rate`}
          Icon={MailCheck}
          accent
        />
        <Score
          testid="report-open-rate"
          label="Open Rate"
          value={`${data.open_rate}%`}
          hint={`${c.unique_open_count || 0} unique opens`}
          Icon={Eye}
        />
        <Score
          testid="report-click-rate"
          label="Click Rate"
          value={`${data.click_rate}%`}
          hint={`${c.unique_click_count || 0} unique clicks`}
          Icon={MousePointerClick}
        />
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Score
          testid="report-bounced"
          label="Bounced"
          value={c.bounced_count || 0}
          hint="Invalid or rejected"
          Icon={AlertTriangle}
        />
        <Score
          testid="report-complained"
          label="Spam Complaints"
          value={c.complained_count || 0}
          hint="Recipient marked as spam"
          Icon={Ban}
        />
        <Score
          testid="report-failed"
          label="Send Errors"
          value={c.failed_count}
          hint="API rejected"
          Icon={MailX}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="report-chart">
          <div className="section-label mb-1">Timeline</div>
          <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
            Hourly activity
          </div>
          <div className="h-[280px] mt-4">
            {data.trend.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#94A3B8]">
                Activity will appear once the campaign is sent.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="hour" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: 12 }} />
                  <Line type="monotone" dataKey="sent" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="open" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="click" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
          <div className="section-label mb-4">Campaign details</div>
          <Detail label="From" value={`${c.from_name || "—"} <${c.from_email || "default"}>`} />
          <Detail label="Reply-to" value={c.reply_to || "—"} />
          <Detail label="Lists" value={c.list_ids?.length || 0} />
          <Detail label="Created" value={new Date(c.created_at).toLocaleString()} />
          {c.sent_at && <Detail label="Sent at" value={new Date(c.sent_at).toLocaleString()} />}
          {c.last_sync_at && <Detail label="Last sync" value={new Date(c.last_sync_at).toLocaleString()} />}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="events-table">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <div className="section-label">Latest events</div>
            <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
              Activity feed
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
            <Send size={12} /> Live updates every 4s
          </div>
        </div>
        {data.events.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#94A3B8]">No activity yet.</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#F8FAFC]">
                <tr className="border-b border-[#E2E8F0]">
                  <Th>Type</Th>
                  <Th>Email</Th>
                  <Th>Details</Th>
                  <Th className="text-right">Time</Th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.id} className="border-b border-[#F1F5F9] hover:bg-slate-50">
                    <td className="px-6 py-3"><TypeBadge type={e.type} /></td>
                    <td className="px-6 py-3 text-sm text-[#0F172A]">{e.email || "—"}</td>
                    <td className="px-6 py-3 text-xs text-[#475569] truncate max-w-md">
                      {e.url || e.error || "—"}
                    </td>
                    <td className="px-6 py-3 text-xs text-right text-[#94A3B8]">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Score = ({ label, value, hint, Icon, accent, testid }) => (
  <div className="scorecard" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="section-label">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent ? "bg-[#E5E9F6] text-[#002FA7]" : "bg-[#F1F5F9] text-[#475569]"}`}>
        <Icon size={15} strokeWidth={2.2} />
      </div>
    </div>
    <div className="text-3xl font-semibold tracking-tight tabular-nums text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
      {value}
    </div>
    <div className="text-xs text-[#94A3B8]">{hint}</div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="py-2.5 border-b border-[#F1F5F9] last:border-0">
    <div className="text-xs text-[#94A3B8] uppercase font-semibold tracking-wider">{label}</div>
    <div className="text-sm text-[#0F172A] mt-0.5 break-words">{value}</div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#64748B] text-left ${className}`}>
    {children}
  </th>
);
